var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);

// src/index.ts
import { proxy, snapshot, subscribe } from "valtio";

// src/storage/localStorage.ts
var LocalStorageStrategy = class {
  constructor() {
    __publicField(this, "isAsync", false);
  }
  has(key) {
    return localStorage.getItem(key) !== null;
  }
  get(key) {
    return localStorage.getItem(key);
  }
  set(key, value) {
    localStorage.setItem(key, value);
  }
  remove(key) {
    localStorage.removeItem(key);
  }
};

// src/merge/default.ts
var DefaultMergeStrategy = class {
  constructor() {
    __publicField(this, "isAsync", false);
  }
  merge(initialState, restoredState) {
    return { ...initialState, ...restoredState };
  }
};

// src/serialization/json/json.ts
import { proxyMap, proxySet } from "valtio/utils";

// src/types.ts
var TYPE_MARKER = {
  Date: "__DATE__",
  Map: "__MAP__",
  Set: "__SET__",
  Symbol: "__SYMBOL__",
  Function: "__FUNCTION__",
  Class: "__CLASS__",
  Error: "__ERROR__",
  DOMElement: "__DOM_ELEMENT__"
};

// src/serialization/json/json.ts
var JSONSerializationStrategy = class {
  constructor() {
    __publicField(this, "isAsync", false);
  }
  serialize(state) {
    const processed = this.processForSerialization(state);
    return JSON.stringify(processed);
  }
  deserialize(data) {
    const parsed = JSON.parse(data);
    return this.processForDeserialization(parsed);
  }
  processForSerialization(obj) {
    if (obj === null || obj === void 0) {
      return obj;
    }
    if (typeof obj !== "object" && typeof obj !== "function" && typeof obj !== "symbol") {
      return obj;
    }
    if (typeof obj === "symbol") {
      return {
        __type: TYPE_MARKER.Symbol,
        value: obj.description
      };
    }
    if (typeof obj === "function") {
      return {
        __type: TYPE_MARKER.Function,
        value: obj.name || "anonymous"
        // Optionally add function.toString() if you want to try reconstructing it
      };
    }
    if (obj instanceof Date) {
      return {
        __type: TYPE_MARKER.Date,
        value: obj.toISOString()
      };
    }
    if (obj instanceof Map) {
      return {
        __type: TYPE_MARKER.Map,
        value: Array.from(obj.entries())
      };
    }
    if (obj instanceof Set) {
      return {
        __type: TYPE_MARKER.Set,
        value: Array.from(obj)
      };
    }
    if (obj instanceof WeakMap || obj instanceof WeakSet) {
      return null;
    }
    if (obj instanceof Error) {
      return {
        __type: TYPE_MARKER.Error,
        value: {
          message: obj.message,
          name: obj.name,
          stack: obj.stack
        }
      };
    }
    if (typeof window !== "undefined" && obj instanceof Element) {
      let selector = obj.tagName.toLowerCase();
      if (obj.id) selector += `#${obj.id}`;
      else if (obj.className)
        selector += `.${obj.className.replace(/\s+/g, ".")}`;
      return {
        __type: TYPE_MARKER.DOMElement,
        value: selector
      };
    }
    if (obj.constructor && obj.constructor !== Object && obj.constructor !== Array) {
      return {
        __type: TYPE_MARKER.Class,
        className: obj.constructor.name,
        value: { ...obj }
        // Convert to plain object
      };
    }
    if (Array.isArray(obj)) {
      return obj.map((item) => this.processForSerialization(item));
    }
    const result = {};
    for (const [key, value] of Object.entries(obj)) {
      result[key] = this.processForSerialization(value);
    }
    return result;
  }
  processForDeserialization(obj) {
    if (obj === null || obj === void 0) {
      return obj;
    }
    if (typeof obj !== "object") {
      return obj;
    }
    const objRecord = obj;
    if ("__type" in objRecord && typeof objRecord.__type === "string") {
      const typeMarker = objRecord.__type;
      switch (typeMarker) {
        case TYPE_MARKER.Date:
          if (typeof objRecord.value === "string") {
            return new Date(objRecord.value);
          }
          break;
        case TYPE_MARKER.Map:
          if (Array.isArray(objRecord.value)) {
            return proxyMap(new Map(objRecord.value));
          }
          break;
        case TYPE_MARKER.Set:
          if (Array.isArray(objRecord.value)) {
            return proxySet(new Set(objRecord.value));
          }
          break;
        case TYPE_MARKER.Symbol:
          if (typeof objRecord.value === "string" || objRecord.value === void 0) {
            return Symbol(objRecord.value);
          }
          break;
        case TYPE_MARKER.Function:
          return () => {
            return void 0;
          };
        case TYPE_MARKER.Error:
          if (typeof objRecord.value === "object" && objRecord.value !== null) {
            const errorValue = objRecord.value;
            const error = new Error(
              typeof errorValue.message === "string" ? errorValue.message : "Unknown error"
            );
            if (typeof errorValue.name === "string") {
              error.name = errorValue.name;
            }
            if (typeof errorValue.stack === "string") {
              error.stack = errorValue.stack;
            }
            return error;
          }
          break;
        case TYPE_MARKER.DOMElement:
          if (typeof objRecord.value === "string" && typeof document !== "undefined") {
            return document.querySelector(objRecord.value);
          }
          return null;
        case TYPE_MARKER.Class:
          if (typeof objRecord.value === "object" && objRecord.value !== null) {
            return objRecord.value;
          }
          break;
        default:
          return objRecord.value;
      }
      return null;
    }
    if (Array.isArray(obj)) {
      return obj.map((item) => this.processForDeserialization(item));
    }
    const result = {};
    for (const [key, value] of Object.entries(objRecord)) {
      result[key] = this.processForDeserialization(value);
    }
    return result;
  }
};

// src/utils.ts
function debounce(fn, ms = 0) {
  let timeoutId = null;
  return function(...args) {
    return new Promise((resolve) => {
      if (timeoutId !== null) {
        clearTimeout(timeoutId);
      }
      timeoutId = setTimeout(() => {
        const result = fn.apply(this, args);
        if (result instanceof Promise) {
          ;
          result.then(() => {
            resolve(result);
          });
        } else {
          resolve(result);
        }
        timeoutId = null;
      }, ms);
    });
  };
}
var updateStore = (store, newState) => {
  for (const key of Object.keys(newState)) {
    store[key] = newState[key];
  }
};

// src/storage/sessionStorage.ts
var SessionStorageStrategy = class {
  constructor() {
    __publicField(this, "isAsync", false);
  }
  has(key) {
    return sessionStorage.getItem(key) !== null;
  }
  get(key) {
    return sessionStorage.getItem(key);
  }
  set(key, value) {
    sessionStorage.setItem(key, value);
  }
  remove(key) {
    sessionStorage.removeItem(key);
  }
};

// src/storage/memoryStorage.ts
var MemoryStorageStrategy = class {
  constructor() {
    __publicField(this, "isAsync", false);
    __publicField(this, "storage", /* @__PURE__ */ new Map());
  }
  has(key) {
    return this.storage.has(key);
  }
  get(key) {
    return this.storage.get(key) || null;
  }
  set(key, value) {
    this.storage.set(key, value);
  }
  remove(key) {
    this.storage.delete(key);
  }
};

// src/index.ts
var isSyncStorage = (storage) => {
  return !storage.isAsync;
};
var isSyncSerializer = (serializer) => {
  return !serializer.isAsync;
};
var isSyncMerger = (merger) => {
  return !merger.isAsync;
};
async function persist(initialState, key, options) {
  const defaultOptions = {
    storageStrategy: LocalStorageStrategy,
    serializationStrategy: JSONSerializationStrategy,
    mergeStrategy: DefaultMergeStrategy,
    shouldPersist: () => true,
    debounceTime: 100
  };
  const o = { ...defaultOptions, ...options };
  const storageInstance = typeof o.storageStrategy === "function" ? new o.storageStrategy() : o.storageStrategy;
  const serializer = typeof o.serializationStrategy === "function" ? new o.serializationStrategy() : o.serializationStrategy;
  const merger = typeof o.mergeStrategy === "function" ? new o.mergeStrategy() : o.mergeStrategy;
  const storage = new Proxy(storageInstance, {
    get(target, prop, receiver) {
      if (prop === "getItem") {
        console.warn("Deprecated: use .get() instead of .getItem()");
        return target.get.bind(target);
      }
      if (prop === "setItem") {
        console.warn("Deprecated: use .set() instead of .setItem()");
        return target.set.bind(target);
      }
      if (prop === "removeItem") {
        console.warn("Deprecated: use .remove() instead of .removeItem()");
        return target.remove.bind(target);
      }
      return Reflect.get(target, prop, receiver);
    }
  });
  const { shouldPersist, debounceTime } = o;
  const data = isSyncStorage(storage) ? storage.get(key) || null : await storage.get(key) || null;
  const storedState = data ? isSyncSerializer(serializer) ? serializer.deserialize(data) || null : await serializer.deserialize(data) || null : null;
  const mergedState = storedState ? isSyncMerger(merger) ? merger.merge(initialState, storedState) || null : await merger.merge(initialState, storedState) || null : void 0;
  const store = proxy(mergedState ?? initialState);
  let previousState = snapshot(store);
  const persistData = async () => {
    const currentState = snapshot(store);
    if (!shouldPersist(previousState, currentState)) {
      return Promise.resolve();
    }
    const serialized = isSyncSerializer(serializer) ? serializer.serialize(currentState) : await serializer.serialize(currentState);
    if (isSyncStorage(storage)) {
      const syncStorage = storage;
      syncStorage.set(key, serialized);
      return Promise.resolve();
    }
    const asyncStorage = storage;
    return asyncStorage.set(key, serialized);
  };
  const debouncedPersist = debounce(persistData, debounceTime);
  subscribe(store, () => {
    const currentState = snapshot(store);
    if (shouldPersist(previousState, currentState)) {
      debouncedPersist();
    }
    previousState = currentState;
  });
  return {
    store,
    persist: persistData,
    clear: async () => {
      if (isSyncStorage(storage)) {
        storage.remove(key);
        return Promise.resolve();
      }
      return storage.remove(key);
    },
    restore: async () => {
      const data2 = isSyncStorage(storage) ? storage.get(key) || null : await storage.get(key) || null;
      const storedState2 = data2 ? isSyncSerializer(serializer) ? serializer.deserialize(data2) || null : await serializer.deserialize(data2) || null : null;
      const mergedState2 = storedState2 ? isSyncMerger(merger) ? merger.merge(initialState, storedState2) || null : await merger.merge(initialState, storedState2) || null : void 0;
      if (mergedState2) {
        updateStore(store, mergedState2);
        return true;
      }
      return false;
    }
  };
}
export {
  LocalStorageStrategy,
  MemoryStorageStrategy,
  SessionStorageStrategy,
  persist
};
//# sourceMappingURL=index.mjs.map